import { getCountableFormatter, getPercentageFormatter } from "../util";
import { ParameterClassification } from "./classification-model";
import { ParameterRange } from "./range.model";
import { FlockValueModel } from "./sheep-flock-value.model";

export function isFlockScenario(o: unknown): o is FlockScenario {
  return "eweMortalityFraction" in (o as FlockScenario);
}

export type FlockScenario = {
  eweMortalityFraction: number;
  scanFraction: number;
  abortionFraction: number;
  lambMortalityBirthTo8Weeks: number;
  growthRateTo8WeeksGramsPerDay: number;
  lambMortality8To12Weeks: number;
  growthRate8To12WeeksGramsPerDay: number;
};

export function isFlockScenarioValid(scenario: FlockScenario) {
  return Object.entries(FlockScenarioRange).every(([key, range]) => {
    const typedKey = key as keyof FlockScenario;
    return (
      key in scenario &&
      scenario[typedKey] >= range.min &&
      scenario[typedKey] <= range.max
    );
  });
}

export type FlockScenarioRangeType = {
  [key in keyof FlockScenario]: ParameterRange;
};

const gramsPerDay = (f: number) => `${f.toFixed(0)} grams/day`;
const eweCountFormatter = getCountableFormatter("ewe", "ewes");
const lambCountFormatter = getCountableFormatter("lamb", "lambs");

export const FlockScenarioRange: Readonly<FlockScenarioRangeType> = {
  eweMortalityFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.1,
    step: 0.01,
    direction: "ltr",
    title: "Annual ewe mortality",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: FlockValueModel) => (v: number) =>
      v / valueModel.breedingMotherInitialHeadCount,
    getReverseTransform: (valueModel: FlockValueModel) => (v: number) =>
      Math.round(v * valueModel.breedingMotherInitialHeadCount),
  },
  scanFraction: {
    unit: "Hd.",
    min: 1,
    max: 2.25,
    step: 0.1,
    direction: "ltr",
    title: "Lambs at scan",
    toolTip:
      "100% means all ewes are pregnant with a single lamb, 200% means all ewes are pregnant with twins",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: FlockValueModel) => (v: number) =>
      v / valueModel.ewesAliveAtScan,
    getReverseTransform: (valueModel: FlockValueModel) => (v: number) =>
      Math.round(v * valueModel.ewesAliveAtScan),
  },
  abortionFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.1,
    step: 0.01,
    direction: "ltr",
    title: "Lambs aborted",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: FlockValueModel) => (v: number) =>
      v / valueModel.lambsAliveAtLambing,
    getReverseTransform: (valueModel: FlockValueModel) => (v: number) =>
      Math.round(v * valueModel.lambsAliveAtLambing),
  },
  lambMortalityBirthTo8Weeks: {
    unit: "Hd.",
    min: 0.0,
    max: 0.2,
    step: 0.01,
    direction: "ltr",
    title: "Lamb mortality in first 8 weeks",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: FlockValueModel) => (v: number) =>
      v / valueModel.lambsBornFullTerm,
    getReverseTransform: (valueModel: FlockValueModel) => (v: number) =>
      Math.round(v * valueModel.lambsBornFullTerm),
  },
  growthRateTo8WeeksGramsPerDay: {
    unit: "g",
    min: 100,
    max: 450,
    step: 10,
    direction: "ltr",
    title: "Growth rate in first 8 weeks (grams/day)",
    forwardTransform: (n) => n,
    reverseTransform: (n) => n,
  },
  lambMortality8To12Weeks: {
    unit: "Hd.",
    min: 0,
    max: 0.05,
    step: 0.005,
    direction: "ltr",
    title: "Lamb mortality, 8 to 12 weeks",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: FlockValueModel) => (v: number) =>
      v / valueModel.lambsAliveAt8Weeks,
    getReverseTransform: (valueModel: FlockValueModel) => (v: number) =>
      Math.round(v * valueModel.lambsAliveAt8Weeks),
  },
  growthRate8To12WeeksGramsPerDay: {
    unit: "g",
    min: 100,
    max: 450,
    step: 10,
    direction: "ltr",
    title: "Growth rate, 8 to 12 weeks (grams/day)",
    forwardTransform: (n) => n,
    reverseTransform: (n) => n,
  },
};

export const BestFlockScenario: Readonly<FlockScenario> = {
  eweMortalityFraction: 0.01,
  scanFraction: 2,
  abortionFraction: 0,
  lambMortalityBirthTo8Weeks: 0.05,
  growthRateTo8WeeksGramsPerDay: 450,
  lambMortality8To12Weeks: 0,
  growthRate8To12WeeksGramsPerDay: 450,
};

export const DefaultFlockScenario: Readonly<FlockScenario> = {
  eweMortalityFraction: 0.03,
  scanFraction: 1.68,
  abortionFraction: 0.01,
  lambMortalityBirthTo8Weeks: 0.08,
  growthRateTo8WeeksGramsPerDay: 300,
  lambMortality8To12Weeks: 0.01,
  growthRate8To12WeeksGramsPerDay: 300,
};

export const WorstFlockScenario: Readonly<FlockScenario> = {
  eweMortalityFraction: 0.1,
  scanFraction: 1,
  abortionFraction: 0.1,
  lambMortalityBirthTo8Weeks: 0.2,
  growthRateTo8WeeksGramsPerDay: 100,
  lambMortality8To12Weeks: 0.05,
  growthRate8To12WeeksGramsPerDay: 100,
};

export const ReferenceFlockScenario: Readonly<FlockScenario> = {
  eweMortalityFraction: 0.02,
  scanFraction: 1.75,
  abortionFraction: 0.01,
  lambMortalityBirthTo8Weeks: 0.07,
  growthRateTo8WeeksGramsPerDay: 300,
  lambMortality8To12Weeks: 0.01,
  growthRate8To12WeeksGramsPerDay: 300,
};

export const SheepClassifications: ParameterClassification[] = [
  {
    id: "1",
    title: "Fertility",
    cardFooter: (
      <span>
        <small>Placeholder text 4. HerdPlan can help you ...</small>
      </span>
    ),
    keys: ["scanFraction", "abortionFraction"],
  },
  {
    id: "2",
    title: "Mortality",
    cardFooter: (
      <span>
        <small>Placeholder text 5. HerdPlan can help you ...</small>
      </span>
    ),
    keys: [
      "eweMortalityFraction",
      "lambMortalityBirthTo8Weeks",
      "lambMortality8To12Weeks",
    ],
  },
  {
    id: "3",
    title: "Growth",
    cardFooter: (
      <span>
        <small>Placeholder text 6. HerdPlan can help you...</small>
      </span>
    ),
    keys: ["growthRateTo8WeeksGramsPerDay", "growthRate8To12WeeksGramsPerDay"],
  },
];
